import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import PreviewCard from './components/PreviewCard';
import EditorPanel from './components/EditorPanel';
import ExportModal from './components/ExportModal';
import './App.css';

const { FiDownload, FiEye, FiEdit3, FiGlobe } = FiIcons;

function App() {
  const [previewMode, setPreviewMode] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  
  // Updated State Structure
  const [bioData, setBioData] = useState({
    name: '',
    title: '',
    about: '',
    avatar: null,
    avatarType: 'preset',
    presetIcon: 'User',
    email: '',
    location: '',
    timezone: '',
    availability: '',
    // Expanded Socials
    socials: { 
      linkedin: '', 
      twitter: '', 
      github: '', 
      instagram: '', 
      youtube: '', 
      tiktok: '', 
      facebook: '',
      dribbble: '' 
    },
    // Projects as Array
    projects: [], 
    learning: [],
    colors: {
      background: '#000000',
      card: '#121212',
      text: '#ffffff',
      accent: '#3b82f6'
    },
    // Enhanced Background Support
    backgroundId: 'clean',
    backgroundType: 'preset', // 'preset', 'solid', 'gradient'
    customBackground: '', // Stores CSS value for solid/gradient
    // Gradient Builder State
    gradientConfig: {
      start: '#0f0c29',
      end: '#302b63',
      dir: 'to bottom right'
    }
  });

  const fileInputRef = useRef(null);

  // Load from localStorage on mount
  useEffect(() => {
    const savedData = localStorage.getItem('bioData');
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        // Merge with default state to ensure new fields (like projects array) exist if loading old data
        setBioData(prev => ({ ...prev, ...parsed }));
      } catch (e) {
        console.error("Failed to load bio data", e);
      }
    }
  }, []);

  // Save to localStorage on change with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
        localStorage.setItem('bioData', JSON.stringify(bioData));
    }, 500); 
    return () => clearTimeout(timeoutId);
  }, [bioData]);

  const handleAvatarUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setBioData(prev => ({
          ...prev,
          avatar: e.target.result,
          avatarType: 'upload'
        }));
      };
      reader.readAsDataURL(file);
    }
  };
  
  const exportBio = () => {
    setShowExportModal(true);
  };

  const resetToHome = () => {
    if(window.confirm("Are you sure you want to reset? This will clear your current bio data from browser storage.")) {
        localStorage.removeItem('bioData');
        window.location.reload();
    }
  };

  return (
    <div className="min-h-screen bg-black text-white font-sans selection:bg-blue-500 selection:text-white overflow-x-hidden">
      
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-black/80 border-b border-white/10 px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          
          <button onClick={resetToHome} className="flex items-center space-x-3 group outline-none">
            <div className="w-9 h-9 bg-gradient-to-br from-zinc-700 to-zinc-900 rounded-lg flex items-center justify-center border border-zinc-700 group-hover:border-zinc-500 transition-colors">
              <SafeIcon icon={FiEdit3} className="text-zinc-300 text-lg group-hover:text-white" />
            </div>
            <span className="hidden sm:block text-lg font-bold tracking-tight text-zinc-300 group-hover:text-white transition-colors">
              Bio Creator
            </span>
          </button>

          <div className="flex items-center space-x-2 sm:space-x-3">
            <motion.button 
              whileHover={{ scale: 1.02 }} 
              whileTap={{ scale: 0.98 }}
              onClick={() => setPreviewMode(!previewMode)}
              className="flex items-center space-x-2 px-3 sm:px-4 py-2 bg-zinc-900 rounded-lg hover:bg-zinc-800 transition-all border border-zinc-800 hover:border-zinc-700 text-sm sm:text-base group"
            >
              <SafeIcon icon={FiEye} className="text-sm group-hover:text-blue-400 transition-colors" />
              <span>{previewMode ? 'Edit' : 'Preview'}</span>
            </motion.button>
            <motion.button 
              whileHover={{ scale: 1.02 }} 
              whileTap={{ scale: 0.98 }}
              onClick={exportBio}
              className="flex items-center space-x-2 px-3 sm:px-4 py-2 bg-white text-black rounded-lg hover:bg-zinc-200 transition-all shadow-[0_0_20px_rgba(255,255,255,0.1)] text-sm sm:text-base font-bold"
            >
              <SafeIcon icon={FiDownload} className="text-sm" />
              <span>Export</span>
            </motion.button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8 relative">
        {previewMode ? (
           <motion.div initial={{opacity: 0}} animate={{opacity: 1}} className="max-w-6xl mx-auto relative z-10" >
              <div className="mb-4 text-center text-sm text-zinc-500 sm:hidden">
                Rotate device for best experience
              </div>
              <PreviewCard bioData={bioData} />
           </motion.div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 relative z-10 items-start">
            {/* Editor Side - Expanded to col-span-5 and removed fixed height for better scrolling */}
            <div className="lg:col-span-5 space-y-6 lg:pr-2">
              <EditorPanel 
                bioData={bioData} 
                setBioData={setBioData} 
                fileInputRef={fileInputRef} 
                handleAvatarUpload={handleAvatarUpload}
              />
            </div>

            {/* Preview Side - Adjusted to col-span-7 */}
            <div className="hidden lg:block lg:col-span-7">
               <div className="sticky top-24 h-[calc(100vh-140px)] flex flex-col">
                  <div className="flex-1 bg-zinc-950/50 backdrop-blur-sm rounded-3xl border border-white/5 p-4 sm:p-8 overflow-hidden relative shadow-2xl flex items-center justify-center">
                     <div className="w-full h-full overflow-y-auto pr-2 scrollbar-hide">
                        <PreviewCard bioData={bioData} />
                     </div>
                  </div>
               </div>
            </div>
          </div>
        )}
      </div>

      {/* Deployment Instructions */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 mt-16 mb-8 relative z-10">
        <div className="bg-gradient-to-b from-zinc-900 to-black border border-zinc-800 rounded-3xl p-6 sm:p-10 relative overflow-hidden">
           <div className="absolute top-0 right-0 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none"></div>
           <div className="relative z-10">
              <h2 className="text-2xl font-bold mb-8 text-center text-white flex items-center justify-center gap-2">
                 <SafeIcon icon={FiGlobe} className="text-blue-500" />
                 <span>Publish Your Site for Free</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 relative">
                 <div className="hidden md:block absolute top-1/2 left-0 w-full h-px bg-gradient-to-r from-transparent via-zinc-700 to-transparent -translate-y-1/2 z-0"></div>
                 {/* Steps */}
                 {[
                    {n:1, t:'Prepare', d:'Fill bio & download HTML. Rename file to index.html', c:'blue'},
                    {n:2, t:'Netlify', d:'Go to Netlify. Click Add new site -> Deploy manually.', c:'blue'},
                    {n:3, t:'Drop', d:'Drag & drop your folder. Your site is Live instantly.', c:'green'}
                 ].map((s, i) => (
                    <div key={i} className={`relative z-10 bg-black border border-zinc-800 p-6 rounded-2xl flex flex-col items-center text-center hover:border-${s.c}-500/50 transition-colors group shadow-lg`}>
                       <div className={`w-12 h-12 rounded-full bg-zinc-900 border border-zinc-800 flex items-center justify-center mb-4 group-hover:bg-${s.c}-600 group-hover:text-white transition-all text-zinc-400 font-bold text-lg`}>{s.n}</div>
                       <h3 className="font-bold text-white mb-2">{s.t}</h3>
                       <p className="text-sm text-zinc-400">{s.d}</p>
                    </div>
                 ))}
              </div>
           </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-900 bg-black py-12 px-6 relative z-10">
         <div className="max-w-4xl mx-auto text-center">
            <p className="text-zinc-600 mb-8 text-sm">© 2025 Ai8848. All rights reserved.</p>
         </div>
      </footer>

      <input ref={fileInputRef} type="file" accept="image/*" onChange={handleAvatarUpload} className="hidden" />
      {showExportModal && <ExportModal bioData={bioData} onClose={() => setShowExportModal(false)} />}
    </div>
  );
}

export default App;